/**
 * BillingPlanSelector — in-page "Choose a plan" view for the Billing page.
 *
 * Ports the plan-selection + payment logic from PackSelectionPage (currency
 * switch, coupon validation, Stripe/Berexia/PAYG branching) MINUS the
 * "tell us about yourself" profile form. Used only by onboarded users
 * upgrading/renewing their plan from /billing.
 *
 * Stripe / Berexia checkouts redirect away and return to /billing?checkout=...
 * PAYG uses the inline card-save flow and stays in-page.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Mail,
  ShieldCheck,
  Ticket,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/atoms/Button';
import {
  getPaidPlans,
  getPlanByCode,
  CARD_SAVE_PACK_CODES,
} from '../../config/plans';
import {
  createPurchaseSession,
  validateCoupon,
  createSetupIntent,
} from '../../services/payment.service';
import { useOnboardingService } from '../../services/onboarding.service';
import { InlineCardInput, type InlineCardHandle } from '../../components/payment/InlineCardInput';
import {
  CurrencyCode,
  formatPrice,
  formatResumePrice,
  formatTalentSearchPrice,
  formatTalentUnlockPrice,
  CONVERSION_MAP,
} from '../../utils/currency';
import { formatCurrency } from '../../utils/locale';
import { mergeLocalizedPlanDisplay } from '../../components/landing/landingContent';
import {
  normalizePurchaseStatus,
  isSuccessStatus,
} from '../../constants/purchaseSession';
import { generateEventId, fireMetaPixelEvent } from '../../services/tracking.service';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/molecules/InputGroup';
import { SegmentedButton } from '@/components/ui/atoms/SegmentedButton';

const CURRENCIES: { code: CurrencyCode; label: string }[] = [
  { code: 'USD', label: 'USD' },
  { code: 'EUR', label: 'EUR' },
  { code: 'MAD', label: 'MAD' },
];

interface BillingPlanSelectorProps {
  /** Return to the billing summary view. */
  onBack: () => void;
  /** Pre-select a plan code (optional). */
  initialPack?: string;
  /** Called after an in-page (PAYG) activation succeeds. */
  onActivated?: () => void;
}

/** Maps the user's current plan to a selectable pack in the upgrade flow. */
export function resolveBillingInitialPack(
  initialPack: string | undefined,
  fallbackPack: string,
): string {
  if (initialPack === 'free_trial') return 'starter';
  if (initialPack && getPlanByCode(initialPack)) return initialPack;
  return fallbackPack;
}

export const BillingPlanSelector: React.FC<BillingPlanSelectorProps> = ({
  onBack,
  initialPack,
  onActivated,
}) => {
  const { t } = useTranslation('settings');
  const { t: tMarketing } = useTranslation('marketing');
  const { user, getAccessTokenSilently } = useAuth0();
  const { activatePayg } = useOnboardingService();

  const paidPlans = getPaidPlans();
  const paygPlanConfig = getPlanByCode('payg');

  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [selectedPack, setSelectedPack] = useState<string>(() =>
    resolveBillingInitialPack(initialPack, paidPlans[0]?.packCode || 'starter'),
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState<number | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);

  const cardRef = useRef<InlineCardHandle>(null);
  const isMAD = currency === 'MAD';
  const isPayg = (CARD_SAVE_PACK_CODES as readonly string[]).includes(selectedPack);
  const needsCard = !isMAD && isPayg;
  const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

  // PAYG is not available for MAD (no card saving with Berexia).
  const showPaygTab = !isMAD && !!paygPlanConfig;
  const availablePlans = useMemo(() => {
    const configs = [
      ...(showPaygTab && paygPlanConfig ? [paygPlanConfig] : []),
      ...paidPlans,
    ];
    return configs.map((config) => mergeLocalizedPlanDisplay(config, tMarketing));
  }, [showPaygTab, paygPlanConfig, paidPlans, tMarketing]);
  const selectedPlan = availablePlans.find((p) => p.packCode === selectedPack) || availablePlans[0];

  // If PAYG selected but MAD chosen, switch away.
  useEffect(() => {
    if (isPayg && isMAD) {
      setSelectedPack(paidPlans[0]?.packCode || 'starter');
    }
  }, [isPayg, isMAD, paidPlans]);

  const formattedPrices = useMemo(() => {
    const all = [...paidPlans, ...(paygPlanConfig ? [paygPlanConfig] : [])];
    return all.reduce<Record<string, string>>((acc, plan) => {
      acc[plan.packCode] = formatPrice(plan.baseAmount, currency, plan.eurAmount);
      return acc;
    }, {});
  }, [currency, paidPlans, paygPlanConfig]);

  const monthlyFormattedPrices = useMemo(() => {
    const all = [...paidPlans, ...(paygPlanConfig ? [paygPlanConfig] : [])];
    return all.reduce<Record<string, string>>((acc, plan) => {
      const monthlyUsdAmount = plan.baseAmount / 12;
      const monthlyEurAmount = plan.eurAmount !== undefined ? plan.eurAmount / 12 : undefined;
      const monthlyAmount =
        currency === 'EUR' && monthlyEurAmount !== undefined
          ? monthlyEurAmount
          : monthlyUsdAmount * (currency === 'MAD' ? 10 : 1);

      acc[plan.packCode] = formatCurrency(Math.ceil(monthlyAmount), currency);
      return acc;
    }, {});
  }, [currency, paidPlans, paygPlanConfig]);

  // Order-summary amounts — display only; mirrors the headline-price conversion.
  const summaryBase =
    currency === 'EUR' && selectedPlan.eurAmount !== undefined
      ? selectedPlan.eurAmount
      : selectedPlan.baseAmount * CONVERSION_MAP[currency];
  const summaryDiscount = couponDiscount ? (summaryBase * couponDiscount) / 100 : 0;
  const summaryNet = summaryBase - summaryDiscount;
  const summaryVat = isMAD ? summaryNet * 0.2 : 0;
  const summaryTotal = summaryNet + summaryVat;
  const fmtAmount = (n: number) => formatCurrency(Math.round(n), currency);
  const summaryPlanDisplay = !isPayg
    ? `${monthlyFormattedPrices[selectedPlan.packCode]} x 12`
    : fmtAmount(summaryBase);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponValidating(true);
    setCouponError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await validateCoupon(token, couponCode.trim(), user?.email);
      if (res.data?.valid) {
        setCouponDiscount(res.data.discount_percent);
        setCouponError(null);
      } else {
        setCouponDiscount(null);
        setCouponError(res.data?.error || t('billing_selector.invalid_coupon'));
      }
    } catch {
      setCouponDiscount(null);
      setCouponError(t('billing_selector.validate_coupon_failed'));
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponDiscount(null);
    setCouponError(null);
    setCouponOpen(false);
  };

  // PAYG: inline card-save flow — stays on /billing.
  const handlePaygPurchase = async () => {
    if (!cardRef.current) {
      toast.error(t('billing_selector.card_not_ready'));
      return;
    }
    setIsProcessing(true);
    try {
      const token = await getAccessTokenSilently();
      // Onboarded users already have a tenant — create SetupIntent directly.
      const setupRes = await createSetupIntent(token);
      if (!setupRes.data?.client_secret) {
        throw new Error(t('billing_selector.card_setup_failed'));
      }
      const cardResult = await cardRef.current.confirmCard(setupRes.data.client_secret);
      if (!cardResult.success) {
        throw new Error(cardResult.error || t('billing_selector.card_verification_failed'));
      }
      await activatePayg();
      try {
        fireMetaPixelEvent('Subscribe', {}, generateEventId());
      } catch {
        // tracking must never break the flow
      }
      toast.success(t('billing_selector.payg_activated'));
      onActivated?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('billing_selector.activate_failed');
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Stripe / Berexia: create a purchase session and redirect to checkout.
  const handleCheckoutPurchase = async () => {
    if (!user?.email) {
      toast.error(t('billing_selector.email_missing'));
      return;
    }
    setIsProcessing(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await createPurchaseSession(token, {
        pack_code: selectedPack,
        currency,
        email: user.email,
        coupon_code: couponCode.trim() || undefined,
        flow: 'billing',
      });
      if (response.error) {
        throw new Error(response.error);
      }
      const status = normalizePurchaseStatus(response.data?.status ?? null);
      if (isSuccessStatus(status)) {
        toast.success(t('billing_selector.pack_activated'));
        onActivated?.();
        return;
      }
      if (!response.data?.checkout_url) {
        throw new Error(t('billing_selector.checkout_failed'));
      }
      // Redirect to provider checkout; provider returns to /billing?checkout=...
      window.location.href = response.data.checkout_url;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('billing_selector.payment_process_failed');
      toast.error(message);
      setIsProcessing(false);
    }
  };

  const handlePurchase = () => {
    if (needsCard) {
      handlePaygPurchase();
      return;
    }
    handleCheckoutPurchase();
  };

  return (
    <div className="max-w-5xl rounded-xl border border-light-lila/40 bg-light overflow-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-light-lila/40 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="icon"
            intent="muted"
            onClick={onBack}
            aria-label={t('billing_selector.back_to_summary')}
            icon={<ArrowLeft />}
          />
          <div>
            <h5 className="text-lg font-semibold text-dark-invirtus font-heading">
              {t('billing_selector.choose_plan')}
            </h5>
            <p className="text-xs text-dark-invirtus/60 font-body">
              {t('billing_selector.choose_plan_desc')}
            </p>
          </div>
        </div>
        {/* Currency selector */}
        <SegmentedButton
          aria-label={t('billing_selector.currency_aria_label', { defaultValue: 'Currency' })}
          size="sm"
          appearance="raised"
          intent="secondary"
          value={currency}
          onValueChange={(next) => setCurrency(next as CurrencyCode)}
          options={CURRENCIES.map((curr) => ({
            value: curr.code,
            label: curr.label,
          }))}
        />
      </div>

      <div className="p-5">
        {/* Plan selector */}
        <SegmentedButton
          aria-label={t('billing_selector.plan_aria_label', { defaultValue: 'Plan' })}
          className="mb-5"
          size="md"
          appearance="filled"
          intent="secondary"
          equalWidth
          value={selectedPack}
          onValueChange={setSelectedPack}
          options={availablePlans.map((plan) => ({
            value: plan.packCode,
            label: plan.planName,
          }))}
        />

        {/* Selected plan detail */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="relative h-full min-h-[27rem] overflow-hidden rounded-[20px] border border-light-lila/40 bg-[linear-gradient(150deg,var(--color-light-red)_0%,var(--color-light-lin)_40%,var(--color-gradient-light)_50%,var(--color-gradient-purple)_100%)] p-6 lg:p-7">
              {/* Dot-grid texture — same visual language as the home & auth pages */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage:
                    'radial-gradient(circle, var(--color-light-lila) 1px, transparent 1px)',
                  backgroundSize: '20px 20px',
                  opacity: 0.35,
                }}
              />
              <div className="relative">
                <h3 className="text-xl font-semibold text-dark-invirtus font-heading">
                  {selectedPlan.planName}
                </h3>
                {isPayg ? (
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-dark-invirtus font-heading">
                      {formattedPrices[selectedPlan.packCode]}
                    </span>
                    {selectedPlan.priceSubtext && (
                      <span className="text-base text-dark-invirtus/70 font-body">
                        {selectedPlan.priceSubtext}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 inline-flex flex-col items-start">
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-bold leading-none text-dark-invirtus font-heading">
                        {monthlyFormattedPrices[selectedPlan.packCode]}
                      </span>
                      <div className="flex flex-col justify-end gap-0.5 pb-1">
                        <span className="text-xs text-dark-invirtus/70 leading-none font-body">
                          {tMarketing('pricing.per_month')}
                        </span>
                        <span className="text-xs text-dark-invirtus/70 leading-none font-body">
                          {tMarketing('pricing.billed_yearly')}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-start w-full">
                      <span className="inline-flex items-center rounded-md border border-dark-invirtus/25 bg-transparent px-3 py-1 text-xs font-semibold text-dark-invirtus/70 whitespace-nowrap">
                        {formattedPrices[selectedPlan.packCode]} {tMarketing('pricing.annual_commitment_badge')}
                      </span>
                    </div>
                  </div>
                )}
                {isMAD && (
                  <p className="mt-1 text-xs text-dark-invirtus/55 font-body">
                    {t('billing_selector.vat_hint')}
                  </p>
                )}
                <p className="mt-2 text-sm font-medium text-dark-invirtus font-body">
                  {selectedPlan.description}
                </p>
                <div className="mt-5 space-y-2.5">
                  {[
                    ...(selectedPlan.features ?? []),
                    ...(isPayg
                      ? [
                          { text: t('billing_selector.per_resume', { price: formatResumePrice(currency) }) },
                          { text: t('billing_selector.per_talent_search', { price: formatTalentSearchPrice(currency) }) },
                          { text: t('billing_selector.per_talent_unlock', { price: formatTalentUnlockPrice(currency) }) },
                        ]
                      : []),
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-dark-invirtus" />
                      <span className="text-sm text-dark-invirtus font-body">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Payment column */}
          <div className="lg:col-span-5">
            <div className="flex h-full flex-col gap-4 rounded-xl border border-light-lila/40 bg-background-gray p-5">
              {/* Order summary */}
              {isPayg ? (
                <div className="rounded-lg border border-light-lila/60 bg-light p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-dark-invirtus/45 font-heading">
                    {selectedPlan.planName}
                  </p>
                  <p className="mt-2 text-2xl font-bold text-dark-invirtus font-heading">
                    {formattedPrices[selectedPlan.packCode]}
                    <span className="ml-1 text-sm font-medium text-dark-invirtus/55">
                      {t('billing_selector.per_interview')}
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
                    {t('billing_selector.payg_desc')}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-light-lila/60 bg-light p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-dark-invirtus/45 font-heading">
                    {t('billing_selector.order_summary')}
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm font-body">
                      <span className="text-dark-invirtus/70">
                        {t('billing_selector.plan_line', { plan: selectedPlan.planName })}
                      </span>
                      <span className="font-medium text-dark-invirtus">
                        {summaryPlanDisplay}
                      </span>
                    </div>
                    {couponDiscount !== null && (
                      <div className="flex items-center justify-between text-sm font-body">
                        <span className="text-dark-invirtus/70">
                          {t('billing_selector.coupon_off', { discount: couponDiscount })}
                        </span>
                        <span className="font-medium text-green">
                          −{fmtAmount(summaryDiscount)}
                        </span>
                      </div>
                    )}
                    {isMAD && (
                      <div className="flex items-center justify-between text-sm font-body">
                        <span className="text-dark-invirtus/70">{t('billing_selector.vat_20')}</span>
                        <span className="font-medium text-dark-invirtus">
                          {fmtAmount(summaryVat)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="my-3 h-px bg-light-lila/50" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-dark-invirtus font-heading">
                      {t('billing_selector.total_due_today')}
                    </span>
                    <span className="text-lg font-bold text-dark-invirtus font-heading">
                      {fmtAmount(summaryTotal)}
                    </span>
                  </div>
                </div>
              )}

              {needsCard && stripePublishableKey && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-dark-invirtus font-heading">
                    {t('billing_selector.card_details')}
                  </label>
                  <div className="rounded-lg border border-light-lila bg-light px-3 py-3 focus-within:border-purple focus-within:ring-1 focus-within:ring-purple">
                    <InlineCardInput ref={cardRef} publishableKey={stripePublishableKey} />
                  </div>
                  <p className="text-xs text-dark-invirtus/55 font-body">
                    {t('billing_selector.card_saved_hint')}
                  </p>
                </div>
              )}

              {/* Coupon — paid checkout plans only */}
              {!isPayg && (
                <div>
                  {couponDiscount !== null ? (
                    <div className="flex items-center justify-between rounded-lg border border-light-lila/60 bg-light px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-dark-invirtus" />
                        <p className="text-sm font-semibold text-dark-invirtus font-body">
                          {couponCode.toUpperCase()} — {couponDiscount}% off
                        </p>
                      </div>
                      <Button
                        type="icon"
                        intent="muted"
                        size="xs"
                        onClick={handleRemoveCoupon}
                        aria-label={t('billing_selector.remove_coupon')}
                        icon={<X />}
                      />
                    </div>
                  ) : couponOpen ? (
                    <div className="space-y-2">
                      <InputGroup size="md" className="w-full">
                        <InputGroupAddon align="inline-start">
                          <Ticket className="h-4 w-4 text-dark-invirtus/60" />
                        </InputGroupAddon>
                        <InputGroupInput
                          type="text"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value);
                            setCouponError(null);
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                          placeholder={t('billing_selector.enter_coupon')}
                          autoFocus
                          startAddonPadding="icon"
                          endAddonPadding="text"
                          aria-label={t('billing_selector.enter_coupon')}
                        />
                        <InputGroupAddon align="inline-end" className="gap-0.5">
                          <InputGroupButton
                            type="contained"
                            intent="secondary"
                            label={couponValidating ? '...' : t('billing_selector.apply')}
                            onClick={handleApplyCoupon}
                            disabled={!couponCode.trim() || couponValidating}
                          />
                          <InputGroupButton
                            type="icon"
                            intent="muted"
                            icon={<X className="h-3.5 w-3.5" />}
                            onClick={() => {
                              setCouponOpen(false);
                              setCouponCode('');
                              setCouponError(null);
                            }}
                            aria-label={t('billing_selector.cancel_coupon')}
                          />
                        </InputGroupAddon>
                      </InputGroup>
                      {couponError && (
                        <p className="text-xs text-red font-body">{couponError}</p>
                      )}
                    </div>
                  ) : (
                    <Button
                      label={t('billing_selector.apply_coupon')}
                      type="text"
                      intent="secondary"
                      size="sm"
                      icon={<Ticket/>}
                      onClick={() => setCouponOpen(true)}
                    />
                  )}
                </div>
              )}

              {/* Trust signals */}
              <div className="space-y-2">
                {[
                  {
                    icon: ShieldCheck,
                      text: t('billing_selector.secure_payment_via', { provider: isMAD ? 'Berexia' : 'Stripe' }),
                  },
                  { icon: Zap, text: t('billing_selector.activates_instantly') },
                  { icon: Mail, text: t('billing_selector.confirmation_emailed') },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5 shrink-0 text-dark-invirtus/45" />
                    <span className="text-xs text-dark-invirtus/60 font-body">
                      {text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-auto">
                <Button
                  label={t('billing_selector.get_plan', { plan: selectedPlan.planName })}
                  type="contained"
                  intent="secondary"
                  size="md"
                  onClick={handlePurchase}
                  loading={isProcessing}
                  className="w-full"
                />
                <p className="mt-2 text-center text-xs text-dark-invirtus/50 font-body">
                  {isPayg
                    ? t('billing_selector.payg_footer')
                    : t('billing_selector.checkout_footer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPlanSelector;
